import { useState, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Text,
  Badge,
  Button,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { DocumentRegular } from '@fluentui/react-icons';
import type { FilingPeriodStatus, FilingPeriodState, FilingDetail, StateTaxObligationSummary } from '../../types';
import { FilingDetailModal } from './FilingDetailModal';
import FilingPeriodActionDialog from './FilingPeriodActionDialog';

/**
 * Compact calendar-year filing grid. Rows = quarters, 3 small colored squares = the
 * months in that quarter (position encodes the month: top-left = Jan); quarterly/
 * semi-annual/annual obligations render one square per period. Pure color + a small
 * status glyph keeps it tiny enough to live in a table cell without overflowing into
 * neighbouring columns — exact month + status are in the hover tooltip / click popover.
 * The legend is rendered once per table via <FilingHistoryLegend/>, not per row.
 */
const useStyles = makeStyles({
  wrap: {
    display: 'inline-flex',
    flexDirection: 'column',
    gap: '3px',
  },
  yearLabel: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    marginBottom: '1px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'auto repeat(3, 16px)',
    gap: '3px',
    alignItems: 'center',
  },
  gridWide: {
    display: 'grid',
    gridTemplateColumns: 'auto 16px',
    gap: '3px',
    alignItems: 'center',
  },
  // v2 mixed-cadence year (mid-year frequency change): one explicit-placement grid where
  // each cell spans the months of its own regime's period — monthly cells 1 column,
  // a quarterly cell 3, a partial period fewer. Same quarter-row geometry as `grid`.
  gridMixed: {
    display: 'grid',
    gridTemplateColumns: 'auto repeat(3, 16px)',
    gridAutoRows: '16px',
    gap: '3px',
    alignItems: 'stretch',
  },
  // Spanning cells fill the grid area they were placed into.
  squareSpan: {
    width: '100%',
    height: '100%',
  },
  // First cell of a new regime — makes the frequency-change boundary visible in the grid.
  segmentBoundary: {
    borderLeftWidth: '3px',
    borderLeftStyle: 'double',
    borderLeftColor: tokens.colorBrandStroke1,
  },
  caption: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    maxWidth: '160px',
  },
  rowLabel: {
    fontSize: tokens.fontSizeBase100,
    lineHeight: '16px',
    color: tokens.colorNeutralForeground4,
    paddingRight: '2px',
  },
  square: {
    width: '16px',
    height: '16px',
    borderRadius: '3px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    padding: 0,
    cursor: 'pointer',
    fontSize: tokens.fontSizeBase100,
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: tokens.colorNeutralForeground3,
    transition: 'transform 80ms ease-in',
    '&:hover': { transform: 'scale(1.18)' },
  },
  filed: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    border: `1px solid ${tokens.colorPaletteGreenBorder1}`,
    color: tokens.colorPaletteGreenForeground2,
  },
  missing: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    border: `1px solid ${tokens.colorPaletteRedBorder1}`,
    color: tokens.colorPaletteRedForeground2,
  },
  dueNow: {
    backgroundColor: tokens.colorPaletteYellowBackground2,
    border: `1px solid ${tokens.colorPaletteYellowBorder1}`,
    color: tokens.colorPaletteYellowForeground2,
  },
  notYetDue: {
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  preEngagement: {
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
  },
  afterEnd: {
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalM,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginTop: tokens.spacingVerticalS,
  },
  swatch: { display: 'inline-flex', alignItems: 'center', gap: '4px' },
  dot: { width: '12px', height: '12px', borderRadius: '3px', display: 'inline-block' },
  popover: {
    minWidth: '240px',
    maxWidth: '320px',
    padding: tokens.spacingVerticalM,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  popHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
  },
  popTitle: { fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold },
  popMeta: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  popFile: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '4px',
    padding: '6px 8px',
    wordBreak: 'break-all',
  },
});

const GLYPH: Record<FilingPeriodState, string> = {
  filed: '✓',
  missing: '✕',
  dueNow: '•',
  notYetDue: '',
  preEngagement: '',
  afterEnd: '',
};

function stateOf(p: FilingPeriodStatus): FilingPeriodState {
  return p.status ?? (p.filed ? 'filed' : 'missing');
}

function useStateClasses() {
  const styles = useStyles();
  const map: Record<FilingPeriodState, string> = {
    filed: styles.filed,
    missing: styles.missing,
    dueNow: styles.dueNow,
    notYetDue: styles.notYetDue,
    preEngagement: styles.preEngagement,
    afterEnd: styles.afterEnd,
  };
  return map;
}

// Build the detail-modal payload from a filed cell. Returns null when the cell isn't filed.
function cellToDetail(p: FilingPeriodStatus, obligationType: string): FilingDetail | null {
  if (!(p.status === 'filed' || (p.filed && !p.status))) return null;
  const periodLabel = p.filedPeriodLabel ?? p.periodLabel;
  if (p.filedSource === 'manual') {
    return {
      source: 'manual',
      obligationType,
      periodLabel,
      filename: p.filename,
      manualFilingId: p.manualFilingId ?? '',
    };
  }
  return {
    source: 'dataverse',
    obligationType,
    periodLabel,
    filename: p.filename,
    activityId: p.activityId,
    slotField: p.slotField,
  };
}

export function FilingHistoryStrip({
  obligation,
  stateTaxClientId,
  clientName,
}: {
  obligation: StateTaxObligationSummary;
  stateTaxClientId: string;
  /** Client's display name (Dataverse account name) — threaded into RecordManualFilingDialog
   * (via FilingPeriodActionDialog) for the Flow C name-mismatch warning. */
  clientName?: string;
}) {
  const styles = useStyles();
  const { t } = useTranslation();
  const stateClass = useStateClasses();
  const [selected, setSelected] = useState<FilingDetail | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [action, setAction] = useState<FilingPeriodStatus | null>(null);
  const history = obligation.recentFilingHistory ?? [];
  if (history.length === 0) return null;

  // periodStart is serialized WITHOUT an offset ("2026-07-01T00:00:00") — JS would parse it
  // as LOCAL time, so Date-based getters shift day-1 dates into the previous month/year for
  // UTC-positive viewers. Derive month/year from the string itself (review 2026-07-20 F3).
  const year = Number(history[0].periodStart.slice(0, 4));
  // v2: cells carry their own month span (per-regime segments). Legacy payloads omit it —
  // fall back to the uniform span implied by the cell count (12→1, 4→3, 2→6, 1→12).
  const defaultSpan = Math.max(1, Math.floor(12 / history.length));
  const spans = history.map((p) => p.monthSpan ?? defaultSpan);
  const uniform = spans.every((s) => s === spans[0]);
  const isMonthly = uniform && spans[0] === 1;

  const openDetail = (p: FilingPeriodStatus) => {
    const detail = cellToDetail(p, obligation.taxType);
    if (!detail) return;
    setSelected(detail);
    setDetailOpen(true);
  };

  const renderSquare = (p: FilingPeriodStatus, placement?: CSSProperties, mixed = false) => {
    const st = stateOf(p);
    const cadenceName =
      mixed && p.segmentCadence
        ? ` (${t(`clientDetailPage.history.cadenceName.${p.segmentCadence}`)})`
        : '';
    const tip = `${p.periodLabel} — ${t(`clientDetailPage.history.state.${st}`)}${cadenceName}`;
    const boundary = mixed && p.segmentStart ? ` ${styles.segmentBoundary}` : '';
    const spanFill = placement ? ` ${styles.squareSpan}` : '';
    return (
      <Popover key={p.periodStart} positioning="below" withArrow>
        <PopoverTrigger disableButtonEnhancement>
          <button
            type="button"
            className={`${styles.square}${spanFill} ${stateClass[st]}${boundary}`}
            style={placement}
            title={tip}
            aria-label={tip}
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={() => setAction(p)}
          >
            <span aria-hidden>{GLYPH[st]}</span>
          </button>
        </PopoverTrigger>
        <PopoverSurface>
          <FilingPeriodPopoverContent period={p} obligation={obligation} onViewDetails={() => openDetail(p)} onAct={(period) => setAction(period)} />
        </PopoverSurface>
      </Popover>
    );
  };

  // Mixed-cadence layout: place each cell explicitly on the 4×3 quarter grid, spanning the
  // months it actually covers (label column is grid column 1).
  const renderPlaced = (p: FilingPeriodStatus, span: number) => {
    // Month from the string, not Date parsing — offset-less datetimes parse as local, which
    // put day-1 periods on the wrong quarter row for UTC-positive viewers (F3).
    const m = Number(p.periodStart.slice(5, 7)) - 1;
    const row = Math.floor(m / 3) + 1;
    const placement: CSSProperties =
      span > 3
        ? { gridRow: `${row} / span ${Math.ceil(span / 3)}`, gridColumn: '2 / span 3' }
        : { gridRow: `${row}`, gridColumn: `${(m % 3) + 2} / span ${span}` };
    return renderSquare(p, placement, true);
  };

  // "Quarterly → Monthly from May 2026" — one entry per mid-year regime boundary.
  const regimeCaptions = uniform
    ? []
    : history
        .map((p, i) => ({ p, prev: history[i - 1] }))
        .filter(({ p }) => p.segmentStart)
        .map(({ p, prev }) =>
          t('clientDetailPage.history.regimeChange', {
            from: prev?.segmentCadence
              ? t(`clientDetailPage.history.cadenceName.${prev.segmentCadence}`)
              : t('common.dash'),
            to: p.segmentCadence
              ? t(`clientDetailPage.history.cadenceName.${p.segmentCadence}`)
              : t('common.dash'),
            period: p.periodLabel,
          }),
        );

  return (
    <div className={styles.wrap}>
      <Text className={styles.yearLabel}>{year}</Text>
      {isMonthly ? (
        <div className={styles.grid}>
          {[0, 1, 2, 3].map((q) => (
            <RowFragment key={q}>
              <Text className={styles.rowLabel}>{t('clientDetailPage.history.quarter', { n: q + 1 })}</Text>
              {history.slice(q * 3, q * 3 + 3).map((p) => renderSquare(p))}
            </RowFragment>
          ))}
        </div>
      ) : uniform ? (
        <div className={styles.gridWide}>
          {history.map((p) => (
            <RowFragment key={p.periodStart}>
              <Text className={styles.rowLabel}>{p.periodLabel.replace(` ${year}`, '')}</Text>
              {renderSquare(p)}
            </RowFragment>
          ))}
        </div>
      ) : (
        <div className={styles.gridMixed}>
          {[0, 1, 2, 3].map((q) => (
            <Text key={`q${q}`} className={styles.rowLabel} style={{ gridRow: q + 1, gridColumn: 1 }}>
              {t('clientDetailPage.history.quarter', { n: q + 1 })}
            </Text>
          ))}
          {history.map((p, i) => renderPlaced(p, spans[i]))}
        </div>
      )}
      {regimeCaptions.length > 0 && (
        <Text className={styles.caption}>{regimeCaptions.join('; ')}</Text>
      )}

      {detailOpen && (
        <FilingDetailModal
          open={detailOpen}
          onOpenChange={setDetailOpen}
          stateTaxClientId={stateTaxClientId}
          detail={selected}
        />
      )}
      <FilingPeriodActionDialog
        open={!!action}
        onOpenChange={(open) => { if (!open) setAction(null); }}
        stateTaxClientId={stateTaxClientId}
        obligations={[obligation]}
        taxType={obligation.taxType}
        periodStart={action?.periodStart ?? ''}
        cadence={action?.segmentCadence ?? obligation.filingFrequencyLabel}
        periodLabel={action?.periodLabel}
        clientName={clientName}
      />
    </div>
  );
}

/** Legend for the filing grid — render once per table, not per row. */
export function FilingHistoryLegend() {
  const styles = useStyles();
  const { t } = useTranslation();
  const stateClass = useStateClasses();
  const states: FilingPeriodState[] = ['filed', 'missing', 'dueNow', 'notYetDue'];
  return (
    <div className={styles.legend}>
      {states.map((st) => (
        <span key={st} className={styles.swatch}>
          <span className={`${styles.dot} ${stateClass[st]}`} />
          {t(`clientDetailPage.history.state.${st}`)}
        </span>
      ))}
    </div>
  );
}

function RowFragment({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

function FilingPeriodPopoverContent({
  period,
  obligation,
  onViewDetails,
  onAct,
}: {
  period: FilingPeriodStatus;
  obligation: StateTaxObligationSummary;
  onViewDetails: () => void;
  onAct: (period: FilingPeriodStatus) => void;
}) {
  const styles = useStyles();
  const { t } = useTranslation();
  const st = stateOf(period);
  const badgeColor =
    st === 'filed' ? 'success' : st === 'missing' ? 'danger' : st === 'dueNow' ? 'warning' : 'subtle';

  return (
    <div className={styles.popover}>
      <div className={styles.popHeader}>
        <Text className={styles.popTitle}>{period.periodLabel}</Text>
        <Badge appearance="filled" color={badgeColor}>
          {t(`clientDetailPage.history.state.${st}`)}
        </Badge>
      </div>
      <Text className={styles.popMeta}>{obligation.taxType}</Text>

      {st === 'filed' && period.filedSource === 'manual' && (
        <Badge appearance="tint" color="warning">{t('clientDetailPage.history.manualBadge')}</Badge>
      )}
      {st === 'filed' && period.filedPeriodLabel && period.filedCadence && (
        <Text className={styles.popMeta}>
          {t('clientDetailPage.history.coveredBy', {
            period: period.filedPeriodLabel,
            cadence: period.filedCadence.toLowerCase(),
          })}
        </Text>
      )}
      {st === 'filed' && period.filename && (
        <>
          <Text className={styles.popMeta}>{t('clientDetailPage.history.uploadedFile')}</Text>
          <Text className={styles.popFile}>{period.filename}</Text>
        </>
      )}
      {st === 'filed' && !period.filename && period.filedSource !== 'manual' && (
        <Text className={styles.popMeta}>{t('clientDetailPage.history.noFileAttached')}</Text>
      )}
      {st === 'filed' && (
        <Button size="small" icon={<DocumentRegular />} onClick={onViewDetails}>
          {t('clientDetailPage.history.viewDetails')}
        </Button>
      )}
      {st === 'missing' && <Text className={styles.popMeta}>{t('clientDetailPage.history.noTaskWarning')}</Text>}
      {st === 'dueNow' && <Text className={styles.popMeta}>{t('clientDetailPage.history.dueNowBody')}</Text>}
      {st === 'notYetDue' && <Text className={styles.popMeta}>{t('clientDetailPage.history.notYetDueBody')}</Text>}
      {st === 'preEngagement' && <Text className={styles.popMeta}>{t('clientDetailPage.history.preEngagementBody')}</Text>}
      {st === 'afterEnd' && <Text className={styles.popMeta}>{t('clientDetailPage.history.afterEndBody')}</Text>}
      {st !== 'filed' && st !== 'afterEnd' && (
        <Button size="small" onClick={() => onAct(period)}>
          {t('clientDetailPage.history.actions.openAction')}
        </Button>
      )}
    </div>
  );
}
